import { useMemo } from "react";
import {
  Box,
  Checkbox,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { numberFormatter } from "../../helper/Reports/common";

const HEADER_MAX_LENGTH = 22;
const CHECKBOX_COL_WIDTH = 52;
const TITLE_ROW_HEIGHT = 56;

function getTruncatedHeaderText(text = "") {
  const value = String(text);
  if (value.length <= HEADER_MAX_LENGTH) return value;
  return `${value.slice(0, HEADER_MAX_LENGTH - 1)}…`;
}

function buildStickyOffsetMap(columns = [], leadingColWidth = 0) {
  const map = {};
  let left = leadingColWidth;
  (columns || []).forEach((column) => {
    if (!column?.isSticky) return;
    map[column.key] = left;
    left += column.minWidth || 120;
  });
  return map;
}

function defaultIsColumnSortable(column) {
  return column?.type === "number";
}

/**
 * Shared sticky MUI insights table shell for Optimizer V2 pages.
 * Pages pass selection rules + optional cell/summary/header renderers.
 */
export default function StickyInsightsTable({
  columns = [],
  rows = [],
  isLoading = false,
  queryOrder = [["spends", "desc"]],
  onSortClick,
  summaryData = {},
  onToggleRow,
  onToggleAll,
  noDataFoundMessage = "No Data Found",
  showCheckbox = true,
  isRowSelectable = () => true,
  getDisabledCheckboxTooltip,
  isColumnSortable = defaultIsColumnSortable,
  getRowKey = (_row, index) => index,
  getAlign,
  dimensionLikeKeys = [],
  renderCell,
  renderSummaryValue,
  renderHeaderContent,
  showSummaryLabel = true,
  footer = null,
  maxHeight = 600,
  minHeight = 400,
}) {
  const sortKey = queryOrder?.[0]?.[0];
  const sortDir = queryOrder?.[0]?.[1] || "desc";
  const leadingColWidth = showCheckbox ? CHECKBOX_COL_WIDTH : 0;
  const stickyOffsetByKey = useMemo(
    () => buildStickyOffsetMap(columns, leadingColWidth),
    [columns, leadingColWidth]
  );

  const selectableRows = useMemo(
    () => (rows || []).filter((row) => isRowSelectable(row)),
    [rows, isRowSelectable]
  );
  const allSelectableChecked =
    selectableRows.length > 0 && selectableRows.every((row) => row?.isChecked);

  const isDimensionLike = (column) =>
    !!column?.isDimension ||
    !!column?.innerKeys ||
    dimensionLikeKeys.includes(column?.key);

  const resolveAlign = (column) => {
    if (getAlign) return getAlign(column);
    return column.alignment || (isDimensionLike(column) ? "left" : "right");
  };

  if (!isLoading && (!rows || rows.length === 0)) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: 250,
          color: "text.secondary",
          gap: 1.5,
          border: "1px solid #ddd",
          borderRadius: "8px",
          mt: 2,
        }}
      >
        <SearchOffIcon sx={{ fontSize: 48, color: "grey.500" }} />
        <Typography variant="subtitle1">{noDataFoundMessage}</Typography>
      </Box>
    );
  }

  const stickyLeftStyle = (left = 0, bgColor = "#ffffff") => ({
    position: "sticky",
    left,
    zIndex: 3,
    background: bgColor,
    backgroundClip: "padding-box",
    boxSizing: "border-box",
    borderRight: "1px solid #ddd",
    boxShadow: "1px 0 0 0 #ddd",
    whiteSpace: "nowrap",
  });

  const checkboxCellStyle = (bgColor = "#ffffff", extra = {}) => ({
    ...stickyLeftStyle(0, bgColor),
    width: CHECKBOX_COL_WIDTH,
    minWidth: CHECKBOX_COL_WIDTH,
    maxWidth: CHECKBOX_COL_WIDTH,
    padding: "0 !important",
    textAlign: "center",
    ...extra,
  });

  const bodyCellBase = {
    fontSize: "0.75rem",
    padding: "14px 16px",
    borderRight: "1px solid #ddd",
    borderBottom: "1px solid #eee",
    boxSizing: "border-box",
  };

  const getStickyLeft = (column) =>
    stickyOffsetByKey[column?.key] ?? leadingColWidth;

  const defaultRenderSummaryValue = (column) => {
    if (isDimensionLike(column)) return "-";
    const value = summaryData?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(
      value,
      !!column?.isCurrencyFormat,
      column?.format,
      column?.maximumFractionDigits
    );
  };

  const renderDimensionCell = (column, row) => {
    const { productName, asin } = column?.innerKeys || {};
    if (productName) {
      const name = row?.[productName] ?? row?.[column.key];
      const asinValue = asin?.value ? row?.[asin.value] : null;
      const displayName = name ?? "-";
      return (
        <Box sx={{ minWidth: 0 }}>
          <Tooltip title={displayName} arrow placement="top">
            <Typography
              sx={{
                fontSize: "12px",
                color: "#333",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: column.minWidth || 220,
              }}
            >
              {displayName}
            </Typography>
          </Tooltip>
          {asinValue ? (
            <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
              {asin?.label ? `${asin.label}: ` : ""}
              {asinValue}
            </Typography>
          ) : null}
        </Box>
      );
    }

    const value = row?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return (
      <Tooltip title={String(value)} arrow placement="top">
        <Typography
          sx={{
            fontSize: "12px",
            color: "#333",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: column.minWidth || 160,
          }}
        >
          {String(value)}
        </Typography>
      </Tooltip>
    );
  };

  const defaultRenderCell = (column, row) => {
    if (isDimensionLike(column)) {
      return renderDimensionCell(column, row);
    }
    const value = row?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(
      value,
      !!column?.isCurrencyFormat,
      column?.format,
      column?.maximumFractionDigits
    );
  };

  const cellRenderer = renderCell || defaultRenderCell;
  const summaryRenderer = renderSummaryValue || defaultRenderSummaryValue;

  const selectStylesForHeader = (isSticky, stickyLeft, isActive) => ({
    ...(isSticky
      ? stickyLeftStyle(stickyLeft, isActive ? "#F7F6FE" : "#fff")
      : {
          background: isActive ? "#F7F6FE" : "#fff",
          borderRight: "1px solid #ddd",
        }),
    position: "sticky",
    top: 0,
    zIndex: isSticky ? 4 : 2,
    color: "#00000099",
    fontWeight: 600,
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
    borderBottom: "none",
    padding: "14px 16px",
    boxSizing: "border-box",
  });

  const renderCheckbox = (row, index) => {
    const selectable = isRowSelectable(row);
    const checkbox = (
      <Checkbox
        size="small"
        checked={!!row?.isChecked}
        disabled={!selectable}
        onChange={() => onToggleRow?.(index)}
        sx={{ p: 0.5 }}
      />
    );
    if (selectable) return checkbox;
    const tip = getDisabledCheckboxTooltip?.(row);
    if (!tip) return checkbox;
    return (
      <Tooltip title={tip} arrow placement="top">
        <span style={{ display: "inline-flex" }}>{checkbox}</span>
      </Tooltip>
    );
  };

  const renderHeaderTitle = (column, sortable) => {
    const titleNode = sortable ? (
      <TableSortLabel
        active={sortKey === column.key}
        direction={sortKey === column.key ? sortDir : "asc"}
        sx={{
          "& .MuiTableSortLabel-icon": {
            color: "#00000099 !important",
          },
        }}
      >
        <Tooltip title={column.shortTitle || ""} arrow placement="top">
          <Typography
            component="span"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#00000099",
            }}
          >
            {getTruncatedHeaderText(column.shortTitle)}
          </Typography>
        </Tooltip>
      </TableSortLabel>
    ) : (
      <Tooltip title={column.shortTitle || ""} arrow placement="top">
        <Typography
          component="span"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#00000099",
          }}
        >
          {getTruncatedHeaderText(column.shortTitle)}
        </Typography>
      </Tooltip>
    );

    const accessory = renderHeaderContent?.(column);
    if (!accessory) return titleNode;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          justifyContent:
            resolveAlign(column) === "left" ? "flex-start" : "flex-end",
        }}
      >
        {titleNode}
        {accessory}
      </Box>
    );
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "auto",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "none",
        mt: 2,
      }}
    >
      <TableContainer sx={{ maxHeight, minHeight }}>
        <Table stickyHeader size="small" sx={{ borderCollapse: "separate" }}>
          <TableHead>
            <TableRow sx={{ height: TITLE_ROW_HEIGHT }}>
              {showCheckbox ? (
                <TableCell
                  sx={checkboxCellStyle("#fff", {
                    zIndex: 4,
                    top: 0,
                    borderBottom: "none",
                  })}
                >
                  <Checkbox
                    size="small"
                    checked={allSelectableChecked}
                    indeterminate={
                      rows.some((row) => row?.isChecked) && !allSelectableChecked
                    }
                    onChange={(e) => onToggleAll?.(e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                </TableCell>
              ) : null}
              {columns.map((column) => {
                const isSticky = !!column?.isSticky;
                const stickyLeft = getStickyLeft(column);
                const colWidth = column.minWidth || 120;
                const sortable = isColumnSortable(column);
                return (
                  <TableCell
                    key={`${column.key}-header`}
                    align={resolveAlign(column)}
                    sx={{
                      ...selectStylesForHeader(
                        isSticky,
                        stickyLeft,
                        sortKey === column.key
                      ),
                      minWidth: colWidth,
                      width: colWidth,
                      maxWidth: isSticky ? colWidth : undefined,
                      cursor: sortable ? "pointer" : "default",
                    }}
                    onClick={() => {
                      if (!sortable) return;
                      onSortClick?.(
                        column.key,
                        sortKey === column.key && sortDir === "asc"
                          ? "desc"
                          : "asc"
                      );
                    }}
                  >
                    {renderHeaderTitle(column, sortable)}
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow
              sx={{
                position: "sticky",
                top: TITLE_ROW_HEIGHT,
                zIndex: 3,
                backgroundColor: "#f9f9f9",
              }}
            >
              {showCheckbox ? (
                <TableCell
                  sx={{
                    ...checkboxCellStyle("#f9f9f9", {
                      zIndex: 5,
                      top: TITLE_ROW_HEIGHT,
                      borderTop: "1px solid #ddd",
                    }),
                  }}
                />
              ) : null}
              {columns.map((column, colIndex) => {
                const isSticky = !!column?.isSticky;
                const stickyLeft = getStickyLeft(column);
                const colWidth = column.minWidth || 120;
                return (
                  <TableCell
                    key={`summary-${column.key}`}
                    align={resolveAlign(column)}
                    sx={{
                      ...(isSticky
                        ? stickyLeftStyle(
                            stickyLeft,
                            sortKey === column.key ? "#EAE6FD" : "#f9f9f9"
                          )
                        : {
                            background:
                              sortKey === column.key ? "#EAE6FD" : "#f9f9f9",
                            borderRight: "1px solid #ddd",
                          }),
                      position: "sticky",
                      top: TITLE_ROW_HEIGHT,
                      zIndex: isSticky ? 5 : 3,
                      minWidth: colWidth,
                      width: colWidth,
                      maxWidth: isSticky ? colWidth : undefined,
                      borderTop: "1px solid #ddd",
                      padding: "14px 16px",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      boxSizing: "border-box",
                    }}
                  >
                    {showSummaryLabel && column.isSticky && colIndex === 0
                      ? "Summary"
                      : isLoading
                        ? "-"
                        : summaryRenderer(column)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {showCheckbox ? (
                      <TableCell
                        sx={{
                          ...bodyCellBase,
                          ...checkboxCellStyle("#fff", { zIndex: 2 }),
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          <Skeleton variant="rectangular" width={18} height={18} />
                        </Box>
                      </TableCell>
                    ) : null}
                    {columns.map((column) => {
                      const colWidth = column.minWidth || 120;
                      return (
                        <TableCell
                          key={`skeleton-${rowIndex}-${column.key}`}
                          sx={{
                            ...bodyCellBase,
                            ...(column.isSticky
                              ? stickyLeftStyle(getStickyLeft(column))
                              : {}),
                            minWidth: colWidth,
                            width: colWidth,
                            maxWidth: column.isSticky ? colWidth : undefined,
                          }}
                        >
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              : rows.map((row, index) => (
                  <TableRow
                    key={getRowKey(row, index)}
                    hover
                    selected={!!row?.isChecked}
                    sx={{
                      "&:hover td": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    {showCheckbox ? (
                      <TableCell
                        sx={{
                          ...bodyCellBase,
                          ...checkboxCellStyle(
                            row?.isChecked ? "#F7F6FE" : "#fff",
                            { zIndex: 2 }
                          ),
                        }}
                      >
                        {renderCheckbox(row, index)}
                      </TableCell>
                    ) : null}
                    {columns.map((column) => {
                      const colWidth = column.minWidth || 120;
                      return (
                        <TableCell
                          key={`${column.key}-${index}`}
                          align={resolveAlign(column)}
                          sx={{
                            ...bodyCellBase,
                            ...(column.isSticky
                              ? stickyLeftStyle(
                                  getStickyLeft(column),
                                  row?.isChecked ? "#F7F6FE" : "#fff"
                                )
                              : {
                                  background: row?.isChecked
                                    ? "#F7F6FE"
                                    : "#fff",
                                }),
                            minWidth: colWidth,
                            width: colWidth,
                            maxWidth: column.isSticky ? colWidth : undefined,
                            zIndex: column.isSticky ? 2 : 1,
                          }}
                        >
                          {cellRenderer(column, row, index)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      {footer}
    </Paper>
  );
}
